import { useMemo, useState } from 'react';
import { Loader2, Search, UserMinus, Users } from 'lucide-react';
import { useClientsQuery } from '@/features/clients/api/useClientsQueries';
import type { ClientUser } from '@/features/clients/api/clientsApi';
import {
  useAssignClientsMutation,
  useAssignedClientsQuery,
  useUnassignAllClientsMutation,
  useUnassignClientsMutation,
} from '../api/useTeamQueries';
import type { AssignedClient } from '../api/teamApi';
import { normalizeAssignedClientList } from '../utils/teamUtils';

function clientLabel(c: AssignedClient | ClientUser) {
  const company =
    typeof c.companyName === 'string' && c.companyName.trim()
      ? c.companyName.trim()
      : '';
  const person = [c.firstName, c.lastName].filter(Boolean).join(' ');
  return company || person || c.email || 'Unnamed client';
}

function clientSubLabel(c: AssignedClient | ClientUser) {
  const company =
    typeof c.companyName === 'string' && c.companyName.trim()
      ? c.companyName.trim()
      : '';
  const person = [c.firstName, c.lastName].filter(Boolean).join(' ');
  if (company && person) return `${person}${c.email ? ` · ${c.email}` : ''}`;
  return c.email ? String(c.email) : '';
}

function resolveAgencyClients(data: unknown): ClientUser[] {
  if (Array.isArray(data)) return data as ClientUser[];
  if (data && typeof data === 'object') {
    const d = data as { users?: ClientUser[]; clients?: ClientUser[] };
    if (Array.isArray(d.users)) return d.users;
    if (Array.isArray(d.clients)) return d.clients;
  }
  return [];
}

type AssignedClientsSectionProps = {
  memberId: string;
};

/** Assign / unassign client dashboards for a team member (post-create). */
export function AssignedClientsSection({ memberId }: AssignedClientsSectionProps) {
  const [selectedToAssign, setSelectedToAssign] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const assignedQ = useAssignedClientsQuery(memberId);
  const agencyClientsQ = useClientsQuery({ page: 1, limit: 100 });
  const assignMut = useAssignClientsMutation();
  const unassignMut = useUnassignClientsMutation();
  const unassignAllMut = useUnassignAllClientsMutation();

  const assigned = useMemo(
    () => normalizeAssignedClientList(assignedQ.data),
    [assignedQ.data],
  );
  const assignedIds = useMemo(
    () => new Set(assigned.map((c) => c._id).filter(Boolean)),
    [assigned],
  );

  const availableClients = useMemo(() => {
    const all = resolveAgencyClients(agencyClientsQ.data?.data);
    const unassigned = all.filter((c) => c._id && !assignedIds.has(c._id));
    const q = search.trim().toLowerCase();
    if (!q) return unassigned;
    return unassigned.filter((c) =>
      [clientLabel(c), clientSubLabel(c), c.email, c.companyName]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(q),
    );
  }, [agencyClientsQ.data, assignedIds, search]);

  const busy =
    assignMut.isPending || unassignMut.isPending || unassignAllMut.isPending;

  const toggleSelect = (clientId: string) => {
    setSelectedToAssign((prev) =>
      prev.includes(clientId)
        ? prev.filter((id) => id !== clientId)
        : [...prev, clientId],
    );
  };

  const handleAssign = () => {
    if (!selectedToAssign.length) return;
    assignMut.mutate(
      { memberId, clientIds: selectedToAssign },
      { onSuccess: () => setSelectedToAssign([]) },
    );
  };

  return (
    <section className="camp-section">
      <header className="mb-4 flex flex-col gap-3 border-b border-[var(--dash-line)] pb-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="text-left">
          <h2 className="m-0 text-base font-semibold text-[var(--dash-ink)]">
            Assigned Clients
          </h2>
          <p className="m-0 mt-0.5 text-sm text-[var(--dash-muted)]">
            Choose which client dashboards this team member can access.
          </p>
        </div>
        {assigned.length > 0 ? (
          <button
            type="button"
            disabled={busy}
            onClick={() => unassignAllMut.mutate(memberId)}
            className="inline-flex h-9 items-center gap-1.5 rounded-xl border border-[var(--dash-line)] px-3 text-xs font-semibold text-[var(--dash-muted)] transition hover:bg-[var(--dash-soft)] hover:text-[var(--dash-ink)] disabled:opacity-50"
          >
            {unassignAllMut.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <UserMinus className="h-3.5 w-3.5" />
            )}
            Unassign all
          </button>
        ) : null}
      </header>

      {assignedQ.isPending ? (
        <div className="flex justify-center py-10">
          <Loader2 className="h-5 w-5 animate-spin text-[var(--dash-brand)]" />
        </div>
      ) : assigned.length === 0 ? (
        <div className="mb-5 flex items-start gap-3 rounded-xl border border-[var(--dash-line)] bg-[var(--dash-soft)] p-4 text-left">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white text-[var(--dash-muted)]">
            <Users className="h-4 w-4" />
          </div>
          <div>
            <p className="m-0 text-sm font-semibold text-[var(--dash-ink)]">
              No clients assigned
            </p>
            <p className="m-0 mt-0.5 text-sm text-[var(--dash-muted)]">
              This member cannot access any client dashboards until you assign
              clients below.
            </p>
          </div>
        </div>
      ) : (
        <ul className="mb-5 m-0 flex list-none flex-col gap-2 p-0">
          {assigned.map((client) => (
            <li
              key={client._id}
              className="flex items-center justify-between gap-3 rounded-xl border border-[var(--dash-line)] bg-white px-3.5 py-3"
            >
              <div className="min-w-0 text-left">
                <p className="m-0 truncate text-sm font-semibold text-[var(--dash-ink)]">
                  {clientLabel(client)}
                </p>
                {clientSubLabel(client) ? (
                  <p className="m-0 truncate text-xs text-[var(--dash-muted)]">
                    {clientSubLabel(client)}
                  </p>
                ) : null}
              </div>
              <button
                type="button"
                disabled={busy}
                onClick={() =>
                  unassignMut.mutate({ memberId, clientIds: [client._id] })
                }
                className="inline-flex h-8 shrink-0 items-center gap-1 rounded-lg px-2.5 text-xs font-semibold text-red-600 transition hover:bg-red-50 disabled:opacity-50"
              >
                Unassign
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="rounded-xl border border-[var(--dash-line)] bg-[var(--dash-soft)]/40 p-4">
        <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <p className="m-0 text-left text-sm font-semibold text-[var(--dash-ink)]">
            Assign clients
          </p>
          {selectedToAssign.length > 0 ? (
            <p className="m-0 text-xs font-medium text-[var(--dash-muted)]">
              {selectedToAssign.length} selected
            </p>
          ) : null}
        </div>

        {agencyClientsQ.isPending ? (
          <div className="camp-skeleton h-28 rounded-xl" />
        ) : resolveAgencyClients(agencyClientsQ.data?.data).filter(
            (c) => c._id && !assignedIds.has(c._id),
          ).length === 0 ? (
          <p className="m-0 text-left text-sm text-[var(--dash-muted)]">
            {assigned.length
              ? 'All agency clients are already assigned.'
              : 'No agency clients available to assign.'}
          </p>
        ) : (
          <>
            <div className="relative mb-3">
              <Search className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-[var(--dash-muted)]" />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search clients by name or email"
                className="h-10 w-full rounded-xl border border-[var(--dash-line)] bg-white pr-3 pl-9 text-sm text-[var(--dash-ink)] outline-none placeholder:text-[var(--dash-muted)] focus:border-[var(--dash-brand)]"
              />
            </div>

            <div className="mb-3 max-h-56 space-y-1 overflow-y-auto rounded-xl border border-[var(--dash-line)] bg-white p-1.5">
              {availableClients.length === 0 ? (
                <p className="m-0 px-2 py-3 text-left text-sm text-[var(--dash-muted)]">
                  No clients match your search.
                </p>
              ) : (
                availableClients.map((client) => {
                  const checked = selectedToAssign.includes(client._id);
                  return (
                    <label
                      key={client._id}
                      className={`flex cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-2 text-left transition ${
                        checked
                          ? 'bg-[var(--dash-brand-soft)]'
                          : 'hover:bg-[var(--dash-soft)]'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleSelect(client._id)}
                        className="h-4 w-4 rounded border-[var(--dash-line)] text-[var(--dash-brand)]"
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium text-[var(--dash-ink)]">
                          {clientLabel(client)}
                        </span>
                        {clientSubLabel(client) ? (
                          <span className="block truncate text-xs text-[var(--dash-muted)]">
                            {clientSubLabel(client)}
                          </span>
                        ) : null}
                      </span>
                    </label>
                  );
                })
              )}
            </div>

            <button
              type="button"
              disabled={busy || selectedToAssign.length === 0}
              onClick={handleAssign}
              className="dash-btn-gradient inline-flex h-10 w-full items-center justify-center gap-2 rounded-xl px-4 text-sm font-semibold text-white disabled:opacity-50 sm:w-auto"
            >
              {assignMut.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : null}
              Assign{' '}
              {selectedToAssign.length > 0 ? selectedToAssign.length : ''}{' '}
              client{selectedToAssign.length === 1 ? '' : 's'}
            </button>
          </>
        )}
      </div>
    </section>
  );
}
